/**
 * PrLiteratureReviewSidebar Component
 *
 * A collapsible left (secondary) sidebar for the PR Literature Review section.
 * It shows five static workflow views (Overview, All PR Articles, Open Author
 * Contacts, Needs Rereview, New Finds) followed by a read-only, collapsible
 * "GBD Cycles" group listing every PR Literature Review collection (the review
 * cycles, e.g. "PR Lit Review GBD2020"). Exactly one option is always selected,
 * defaulting to the Overview.
 *
 * Uses the generic SecondarySideBar for the open/close toggle and layout, and
 * mirrors the journal articles secondary sidebar's expanded + collapsed-rail
 * presentation. Unlike that sidebar, the GBD Cycles group is read-only: there
 * is no create / manage affordance.
 */

import { useEffect, useState } from "react"
import {
  Mail,
  FileText,
  Book,
  RefreshCw,
  ChevronDown,
  BookOpen,
  RotateCcw,
  Sparkles,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover"
import { Popover as PopoverPrimitive } from "radix-ui"
import SecondarySideBar from "@/components/shared/SecondarySideBar"
import { BiblioCollection, PR_LITERATURE_REVIEW_TYPE } from "@/services/biblioService"
import LoadingSpinner from "@/components/shared/LoadingSpinner"

/**
 * All possible sidebar option identifiers for the PR Literature Review section.
 * GBD cycle (collection) options use the pattern "collection-{id}" with a
 * numeric ID.
 */
export type SidebarOption =
  | "all-pr-articles"
  | "overview"
  | "open-author-contacts"
  | "needs-rereview"
  | "new-finds"
  | `collection-${number}`

/** The default sidebar selection when no other option has been chosen. */
export const DEFAULT_SIDEBAR_OPTION: SidebarOption = "overview"

/**
 * Pattern for a cycle (collection) sidebar option ("collection-{id}"),
 * capturing the numeric id. Single source of truth for the collection-option
 * encoding so the convention is not re-spelled across the codebase.
 */
const COLLECTION_OPTION_RE = /^collection-(\d+)$/

/**
 * Extract the numeric collection id from a cycle sidebar option.
 *
 * @param option - The sidebar option to inspect (e.g. "collection-42").
 * @returns The collection's numeric id, or null if the option is not a cycle
 *   tab.
 */
export function collectionIdFromOption(option: SidebarOption): number | null {
  const match = option.match(COLLECTION_OPTION_RE)
  return match ? parseInt(match[1], 10) : null
}

/** Display metadata for each static (non-cycle) sidebar option. */
const OPTION_META: Partial<Record<SidebarOption, { label: string; icon: React.ReactNode }>> = {
  "all-pr-articles": {
    label: "All PR Articles",
    icon: <BookOpen className="size-4" />,
  },
  overview: {
    label: "Overview",
    icon: <FileText className="size-4" />,
  },
  "open-author-contacts": {
    label: "Open Author Contacts",
    icon: <Mail className="size-4" />,
  },
  "needs-rereview": {
    label: "Needs Rereview",
    icon: <RotateCcw className="size-4" />,
  },
  "new-finds": {
    label: "New Finds",
    icon: <Sparkles className="size-4" />,
  },
}

/**
 * Returns the human-readable label for a static (non-cycle) sidebar option.
 *
 * Cycle options (`collection-{id}`) are intentionally not covered here, since
 * their label is the dynamic collection name — resolve those from the loaded
 * collections list instead.
 *
 * @param option - The sidebar option to look up.
 * @returns The option's display label, or undefined if it has no static label
 *   (e.g. a cycle option).
 */
export function getSidebarOptionLabel(option: SidebarOption): string | undefined {
  return OPTION_META[option]?.label
}

/**
 * The static options rendered as a flat list at the top of the sidebar, in
 * display order: Overview, All PR Articles, Open Author Contacts, Needs
 * Rereview, New Finds.
 */
const STATIC_OPTIONS: SidebarOption[] = [
  "overview",
  "all-pr-articles",
  "open-author-contacts",
  "needs-rereview",
  "new-finds",
]

interface PrLiteratureReviewSidebarProps {
  /** The currently active sidebar option. */
  selectedOption: SidebarOption
  /** Handler called when a new option is selected. */
  onOptionChange: (option: SidebarOption) => void
  /** Collections fetched from the API (the Cycle list is derived from these). */
  collections: BiblioCollection[]
  /** Whether collections are still loading. */
  collectionsLoading: boolean
  /** Whether the sidebar starts expanded (uncontrolled mode). */
  defaultOpen?: boolean
  /** Controlled mode: whether the sidebar is expanded. */
  isOpen?: boolean
  /** Controlled mode: callback to toggle the sidebar's expanded state. */
  onToggle?: () => void
}

/**
 * Collapsible secondary sidebar for the PR Literature Review section.
 *
 * @param props - Component props.
 * @returns The rendered sidebar element.
 */
export default function PrLiteratureReviewSidebar({
  selectedOption,
  onOptionChange,
  collections,
  collectionsLoading,
  defaultOpen = true,
  isOpen,
  onToggle,
}: PrLiteratureReviewSidebarProps): React.JSX.Element {
  // Whether the Cycle group is expanded in the full (non-collapsed) view.
  const [cycleOpen, setCycleOpen] = useState<boolean>(true)

  // The PR Literature Review collections (the review cycles). These are the
  // only collections this section surfaces.
  const cycleCollections: BiblioCollection[] = collections.filter(
    (collection) => collection.collectionType === PR_LITERATURE_REVIEW_TYPE
  )

  // When the selected option is a cycle collection (e.g. after a page refresh
  // while viewing a cycle), auto-expand the Cycle group so the selected cycle
  // is visible. Only ever opens the group (never forces it closed), so a user's
  // manual collapse is preserved.
  useEffect(() => {
    if (collectionIdFromOption(selectedOption) !== null) {
      setCycleOpen(true)
    }
  }, [selectedOption])

  /**
   * Sorts a list of collections alphabetically by name without mutating the
   * input.
   *
   * @param toSort - The collections to sort.
   * @returns A new array sorted by `collectionName` ascending.
   */
  const sortByName = (toSort: BiblioCollection[]): BiblioCollection[] =>
    [...toSort].sort((a, b) => a.collectionName.localeCompare(b.collectionName))

  /**
   * Returns the Tailwind class string for an expanded-view sidebar button.
   *
   * @param option - The sidebar option the button represents.
   * @returns The computed className string.
   */
  const getButtonClassName = (option: SidebarOption): string =>
    cn(
      "text-left text-xs px-2 py-1 rounded-md transition-colors w-full",
      option === selectedOption ? "bg-accent text-accent-foreground font-medium" : "hover:bg-muted"
    )

  /**
   * Renders an expanded-view sidebar button for a static option.
   *
   * @param option - The sidebar option identifier.
   * @param label - The display label for the button.
   * @returns The rendered button.
   */
  const renderSidebarButton = (option: SidebarOption, label: string): React.JSX.Element => {
    const icon = OPTION_META[option]?.icon ?? null
    return (
      <button type="button" className={getButtonClassName(option)} onClick={() => onOptionChange(option)}>
        <span className="flex items-center gap-2">
          {icon}
          {label}
        </span>
      </button>
    )
  }

  /**
   * Renders an expanded-view button for a single cycle (collection).
   *
   * @param collection - The collection to render.
   * @returns The rendered cycle button.
   */
  const renderCycleButton = (collection: BiblioCollection): React.JSX.Element => {
    const option: SidebarOption = `collection-${collection.id}`
    return (
      <button
        key={collection.id}
        type="button"
        className={cn(
          "text-left text-xs px-2 py-1 rounded-md transition-colors w-full flex items-center gap-2",
          option === selectedOption
            ? "bg-accent text-accent-foreground font-medium"
            : "hover:bg-muted"
        )}
        onClick={() => onOptionChange(option)}
      >
        <Book className="size-3.5 shrink-0" />
        <span className="truncate">{collection.collectionName}</span>
      </button>
    )
  }

  /**
   * Renders a collapsed-rail icon button for a static option, wrapped in a
   * tooltip showing the option label.
   *
   * @param option - The sidebar option identifier.
   * @returns The rendered collapsed button.
   */
  const renderCollapsedButton = (option: SidebarOption): React.JSX.Element => {
    const meta = OPTION_META[option]!
    return (
      <Tooltip key={option}>
        <TooltipTrigger asChild>
          <span className="w-full flex justify-center">
            <button
              type="button"
              onClick={() => onOptionChange(option)}
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded transition-colors",
                option === selectedOption
                  ? "bg-accent text-accent-foreground"
                  : "hover:bg-muted cursor-pointer"
              )}
            >
              {meta.icon}
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="right">{meta.label}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <SecondarySideBar
      defaultOpen={defaultOpen}
      isOpen={isOpen}
      onToggle={onToggle}
      collapsedContent={
        <>
          {STATIC_OPTIONS.map((option) => renderCollapsedButton(option))}

          {/* GBD Cycles group — popover with all cycle (collection) icons. */}
          <Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center justify-center w-8 h-8 rounded hover:bg-muted cursor-pointer transition-colors mx-auto"
                  >
                    <RefreshCw className="size-4" />
                  </button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">GBD Cycles</TooltipContent>
            </Tooltip>
            <PopoverContent side="right" align="start" className="w-52 gap-0 p-1">
              <PopoverPrimitive.Arrow
                width={12}
                height={6}
                className="fill-popover stroke-(--color-foreground)/12 stroke-[3px] [paint-order:stroke_fill]"
              />
              <p className="px-2 py-1 text-xs font-semibold text-muted-foreground">GBD Cycles</p>
              {collectionsLoading ? (
                <LoadingSpinner variant={"default"} loadingContent={"Loading..."} />
              ) : cycleCollections.length === 0 ? (
                <p className="px-2 py-1 text-xs text-muted-foreground">No cycles</p>
              ) : (
                sortByName(cycleCollections).map((collection) => {
                  const option: SidebarOption = `collection-${collection.id}`
                  return (
                    <button
                      key={collection.id}
                      type="button"
                      onClick={() => onOptionChange(option)}
                      className={cn(
                        "flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded transition-colors",
                        option === selectedOption
                          ? "bg-accent text-accent-foreground font-medium"
                          : "hover:bg-accent"
                      )}
                    >
                      <Book className="size-3.5 shrink-0" />
                      <span className="truncate">{collection.collectionName}</span>
                    </button>
                  )
                })
              )}
            </PopoverContent>
          </Popover>
        </>
      }
    >
      <div className="flex flex-col gap-0.5">
        {STATIC_OPTIONS.map((option) => (
          <span key={option}>{renderSidebarButton(option, OPTION_META[option]!.label)}</span>
        ))}
      </div>

      {/* GBD Cycles group — collapsible, read-only, dynamically populated. */}
      <Collapsible open={cycleOpen} onOpenChange={setCycleOpen}>
        <CollapsibleTrigger className="flex items-center justify-between w-full px-2 py-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground rounded-md transition-colors cursor-pointer">
          <span className="flex items-center gap-2">
            <RefreshCw className="size-4" />
            GBD Cycles
          </span>
          <ChevronDown
            className={cn(
              "size-4 transition-transform duration-200",
              cycleOpen ? "rotate-0" : "-rotate-90"
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col gap-0.5 py-1 pl-3">
            {collectionsLoading ? (
              <LoadingSpinner variant={"default"} loadingContent={"Loading..."} />
            ) : cycleCollections.length === 0 ? (
              <span className="text-xs text-muted-foreground px-2 py-1">No cycles</span>
            ) : (
              sortByName(cycleCollections).map((collection) => renderCycleButton(collection))
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </SecondarySideBar>
  )
}
